import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { PAGE_COLORS } from '../../utils/pageColors';
import { Calendar, CalendarSkeleton, CalendarError, JobDetailsModal, CalendarFilters } from '../../components/calendar';
import type { CalendarEvent } from '../../types/calendar';
import { useGetCalendarJobsQuery, useRescheduleJobMutation } from '../../store/slices/apiSlice';
import { transformJobToCalendarEvent } from '../../utils/calendarHelpers';
import { getJobStatusBadge } from '../../utils/jobStatusMapper';
import { isJobLocked, getLockMetadata } from '../../utils/lockHelpers';
import { validateDeliveryDate } from '../../utils/deliveryDateValidation';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { useToast } from '../../hooks/useToast';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { collapseAllEvents } from '../../store/slices/calendarUISlice';

export const DeliveryCalendarPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null);
  const [selectedDeliveryUser, setSelectedDeliveryUser] = useState<string>('all');
  const [selectedFulfillmentType, setSelectedFulfillmentType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const toast = useToast();
  
  // Fetch jobs for current month view with delivery user and fulfillment type filters
  const { data: jobs, isLoading, error, refetch } = useGetCalendarJobsQuery({
    start_date: format(startOfMonth(currentDate), 'yyyy-MM-dd'),
    end_date: format(endOfMonth(currentDate), 'yyyy-MM-dd'),
    delivery_user: selectedDeliveryUser,
    fulfillment_type: selectedFulfillmentType,
  });
  
  // Reschedule job mutation
  const [rescheduleJob, { isLoading: isRescheduling }] = useRescheduleJobMutation();
  
  // Transform jobs to calendar events and sort by time for proper sequencing
  const events = useMemo(() => {
    if (!jobs) return [];
    const allEvents = jobs
      .filter(job => job.potential_delivery_date)
      .map(transformJobToCalendarEvent)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    if (selectedStatus === 'all') return allEvents;

    return allEvents.filter((event) => {
      const job = event.notes?.job;
      if (!job) return false;
      return (
        getJobStatusBadge({
          status: job.status,
          production_status: job.production_status,
          fulfillment_type: job.fulfillment_type,
          delivery_result: job.delivery_result,
          collection_completed: job.collection_completed,
        }) === selectedStatus
      );
    });
  }, [jobs, selectedStatus]);
  
  // For month view same-day reordering, we'll use a simple approach:
  // Prompt user to use week/day view for precise reordering
  // OR implement up/down buttons in the future
  
  // Handle drag and drop with same-day vs different-day logic
  const handleEventDrop = useCallback(async (event: CalendarEvent, start: Date, _end: Date, dropInfo?: any) => {
    const jobId = event.notes?.jobId;
    const oldDate = event.start;
    
    // Check lock status with date-aware logic
    const lockData = event.notes?.job?.lock_data;
    if (isJobLocked(lockData)) {
      // Check if it's same-day vs different-day
      const isSameDay = format(oldDate, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd');
      
      console.log('🔒 LOCK CHECK:', {
        jobId,
        oldDate: format(oldDate, 'yyyy-MM-dd HH:mm:ss'),
        newDate: format(start, 'yyyy-MM-dd HH:mm:ss'),
        oldDateFormatted: format(oldDate, 'yyyy-MM-dd'),
        newDateFormatted: format(start, 'yyyy-MM-dd'),
        isSameDay,
        lockReason: lockData?.reason
      });
      
      if (!isSameDay) {
        // ❌ BLOCK: Different day drag
        const meta = getLockMetadata(lockData);
        toast.error(
          'Date Change Blocked',
          `This job's date is locked and cannot be changed.\n\n` +
          `Reason: ${meta?.reason}\n` +
          `Locked by: ${meta?.lockedBy}\n\n` +
          `💡 Tip: You can still reorder it by changing the time on the same day.`,
          { duration: 8000 }
        );
        return;
      }
      
      // ✅ ALLOW: Same day - continue with time change
      console.log(`🔓 Locked job ${jobId} - allowing same-day time reorder`);
    }
    
    if (!jobId) {
      toast.error('Error', 'Invalid job data');
      return;
    }
    
    // Detect same-day vs different-day drag
    const isSameDay = format(oldDate, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd');
    
    console.log('=== RESCHEDULE DEBUG ===');
    console.log('Job ID:', jobId);
    console.log('Old Date:', oldDate);
    console.log('New Date:', start);
    console.log('Same Day?:', isSameDay);
    console.log('Drop Info:', dropInfo);
    
    let formattedDate: string;
    
    if (isSameDay) {
      // Same day reordering - calculate position based on visual stack
      // Get all jobs on the same day sorted by current time
      const sameDayJobs = events
        .filter(e => format(e.start, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd'))
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      
      // Find the dragged event's OLD position
      const oldIndex = sameDayJobs.findIndex(e => e.id === event.id);
      
      // Check if we're in month view or week/day view
      const isMonthView = dropInfo?.view === 'month';
      
      if (isMonthView) {
        // Month view - React Big Calendar doesn't support reordering within same day cell
        // Show helpful message to user
        console.log('Month view - Same day reordering not supported by React Big Calendar');
        toast.info('Tip', 'To reorder jobs on the same day, please use Week or Day view for precise control');
        return;
      }
      
      // Week/Day view - Calculate new position based on drop time
      // Round to nearest 5-minute interval
      const newTime = new Date(start);
      const minutes = newTime.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      newTime.setMinutes(roundedMinutes);
      newTime.setSeconds(0);
      newTime.setMilliseconds(0);
      
      // Find where this time would fit in the sorted list
      let newIndex = sameDayJobs.findIndex(e => e.start.getTime() > newTime.getTime());
      if (newIndex === -1) {
        // Dropped after all existing jobs
        newIndex = sameDayJobs.length;
      }
      
      // Adjust index if we're moving the same job
      if (oldIndex < newIndex) {
        newIndex--;
      }
      
      console.log('📍 Reorder calculation:', {
        oldIndex,
        newIndex,
        totalJobs: sameDayJobs.length,
        oldTime: format(oldDate, 'HH:mm'),
        newTime: format(newTime, 'HH:mm'),
        roundedTo5Min: true
      });
      
      formattedDate = newTime.toISOString();
      console.log('✅ Week/Day view - Sending time:', format(newTime, 'HH:mm'));
    } else {
      // Different day - Round to nearest 5-minute interval
      const newTime = new Date(start);
      const minutes = newTime.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      newTime.setMinutes(roundedMinutes);
      newTime.setSeconds(0);
      newTime.setMilliseconds(0);
      
      formattedDate = newTime.toISOString();
      console.log('Different-day move - Sending time:', format(newTime, 'HH:mm'), '(rounded to 5min)');
    }
    
    console.log('Formatted:', formattedDate);
    
    // Validate delivery date before rescheduling
    const job = event.notes?.job;
    const validationError = validateDeliveryDate({
      potentialDeliveryDate: formattedDate,
      manufactureDate: job?.manufacture_date,
      glassArrivalDate: job?.glass_arrival_date,
      jobStatus: job?.status,
      productionStatus: job?.production_status
    });
    
    if (validationError) {
      toast.error('Date Validation Error', validationError);
      return;
    }
    
    try {
      const result = await rescheduleJob({
        id: jobId,
        potential_delivery_date: formattedDate,
      }).unwrap();
      
      console.log('✅ Reschedule SUCCESS:', result);
      
      const actionType = isSameDay ? 'reordered' : 'rescheduled';
      toast.success('Success', `Delivery ${actionType} to ${format(start, 'MMM dd, yyyy')}`);
      
      // Refetch to ensure UI is in sync
      refetch();
    } catch (err: any) {
      console.error('❌ Reschedule ERROR:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      
      const errorMsg = err?.data?.detail || err?.data?.message || err?.message || 'Failed to reschedule delivery';
      toast.error('Reschedule Failed', errorMsg);
    }
  }, [rescheduleJob, toast, events, refetch]);
  
  // Handle drag over for visual feedback on locked jobs
  const handleDragOver = useCallback((args: any) => {
    const { event, start } = args;
    const lockData = event.notes?.job?.lock_data;
    
    if (isJobLocked(lockData)) {
      const oldDate = event.start;
      const isSameDay = format(oldDate, 'yyyy-MM-dd') === format(start, 'yyyy-MM-dd');
      
      // Return style/class for invalid drop zone
      if (!isSameDay) {
        return {
          className: 'invalid-drop-zone',
          style: { 
            cursor: 'not-allowed',
            opacity: 0.5,
            border: '2px dashed red'
          }
        };
      }
    }
    
    return {};
  }, []);
  
  // Handle reorder up/down
  const handleMoveUp = useCallback(async (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    const eventDate = event.start;
    const sameDayEvents = events
      .filter(e => format(e.start, 'yyyy-MM-dd') === format(eventDate, 'yyyy-MM-dd'))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    
    const currentIndex = sameDayEvents.findIndex(e => e.id === eventId);
    if (currentIndex <= 0) return; // Already at top
    
    // Calculate new time (move to position above)
    const targetEvent = sameDayEvents[currentIndex - 1];
    const newTime = new Date(targetEvent.start);
    newTime.setMinutes(newTime.getMinutes() - 5); // 5 minutes before the target (one slot)
    
    try {
      await rescheduleJob({
        id: eventId,
        potential_delivery_date: newTime.toISOString(),
      }).unwrap();
      
      toast.success('Success', 'Job moved up in order');
      refetch();
    } catch (err: any) {
      const errorMsg = err?.data?.detail || 'Failed to reorder job';
      toast.error('Reorder Failed', errorMsg);
    }
  }, [events, rescheduleJob, toast, refetch]);
  
  const handleMoveDown = useCallback(async (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    const eventDate = event.start;
    const sameDayEvents = events
      .filter(e => format(e.start, 'yyyy-MM-dd') === format(eventDate, 'yyyy-MM-dd'))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    
    const currentIndex = sameDayEvents.findIndex(e => e.id === eventId);
    if (currentIndex >= sameDayEvents.length - 1) return; // Already at bottom
    
    // Calculate new time (move to position below)
    const targetEvent = sameDayEvents[currentIndex + 1];
    const newTime = new Date(targetEvent.start);
    newTime.setMinutes(newTime.getMinutes() + 5); // 5 minutes after the target (one slot)
    
    try {
      await rescheduleJob({
        id: eventId,
        potential_delivery_date: newTime.toISOString(),
      }).unwrap();
      
      toast.success('Success', 'Job moved down in order');
      refetch();
    } catch (err: any) {
      const errorMsg = err?.data?.detail || 'Failed to reorder job';
      toast.error('Reorder Failed', errorMsg);
    }
  }, [events, rescheduleJob, toast, refetch]);
  
  // Handle date changes when navigating calendar
  // Collapse all expanded events when navigating to a different date
  const handleDateChange = useCallback((date: Date) => {
    setCurrentDate(date);
    dispatch(collapseAllEvents());
  }, [dispatch]);
  
  // Show loading state
  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="Delivery Calendar"
          subtitle="Plan and schedule deliveries"
          bgColor={PAGE_COLORS.delivery.bgColor}
          textColor={PAGE_COLORS.delivery.textColor}
          borderColor={PAGE_COLORS.delivery.borderColor}
          icon={<CalendarIcon className="w-6 h-6" />}
        >
          <CalendarSkeleton />
        </PageLayout>
      </AppShell>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <AppShell>
        <PageLayout
          title="Delivery Calendar"
          subtitle="Plan and schedule deliveries"
          bgColor={PAGE_COLORS.delivery.bgColor}
          textColor={PAGE_COLORS.delivery.textColor}
          borderColor={PAGE_COLORS.delivery.borderColor}
          icon={<CalendarIcon className="w-6 h-6" />}
        >
          <CalendarError error={error} onRetry={refetch} />
        </PageLayout>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageLayout
        title="Delivery Calendar"
        subtitle={`Plan and schedule deliveries - ${events.length} scheduled`}
        bgColor={PAGE_COLORS.delivery.bgColor}
        textColor={PAGE_COLORS.delivery.textColor}
        borderColor={PAGE_COLORS.delivery.borderColor}
        icon={<CalendarIcon className="w-6 h-6" />}
      >
        {/* Filter Section */}
        <div className="mb-4">
          <CalendarFilters
            selectedDeliveryUser={selectedDeliveryUser}
            onDeliveryUserChange={setSelectedDeliveryUser}
            selectedFulfillmentType={selectedFulfillmentType}
            onFulfillmentTypeChange={setSelectedFulfillmentType}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
          />
        </div>

        {/* Calendar - auto-height container (page scroll handles long months) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible" style={{ minHeight: '700px' }}>
          <Calendar
            events={events}
            defaultView="month"
            onEventDrop={handleEventDrop}
            onDragOver={handleDragOver}
            onDateChange={handleDateChange}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
          />
        </div>
      </PageLayout>
    </AppShell>
  );
};
